import { useState, useEffect } from 'react'
import { formatBetNation, formatBetOdds } from '../../utils/casinoUtils'

// Bets block shared by the Account Statement "Details" modal and the casino
// Result modal: All / Back / Lay / Deleted radios, Total Bets / Total Win, then
// Username / Nation / Rate / (Bhav) / Amount / Win / Date / IP / B Details /
// Action. Ticking rows narrows the totals to them; none ticked = every visible
// row. Bet shape: { id, username, nation, rate, bhav?, amount, win, date, ip,
// browser, side, deleted, badlo? } (win null = unsettled / deleted).
// Plan: docs/plans/2026-10-09-admin-casino-result-round-bets.md

const FILTERS = [['all', 'All'], ['back', 'Back'], ['lay', 'Lay'], ['deleted', 'Deleted']]

const isLay = (b) => String(b.side || '').toUpperCase() === 'LAY'

const amount = (v) => {
  if (v == null || v === '') return ''
  const n = Number(v)
  return Number.isFinite(n) ? n.toLocaleString() : String(v)
}

const pad = (n) => String(n).padStart(2, '0')

// Bet dates are real timestamps -> browser local time
const fmtDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d)) return String(value)
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// idPrefix keeps radio / checkbox ids unique per modal.
// `gameType` (casino game code) picks the display name format — Note Number
// card bets read "Card 2 - Card A". Omitted on sports rows → printed as stored.
// `emptyRow` = false drops the whole table (header + "No records found" row)
// when a filter matches no bet — only the radios and totals stay (casino Result
// modal, user 2026-10-14); the statement modal keeps the table and the message.
// `detail` = the round's result (t1); cmeter1's Rate is read from it.
function RoundBetsTable({ bets = [], showBhav = false, idPrefix = 'round-bet', gameType = '', emptyRow = true, detail = null }) {
  // Virtual tables that format like their base: V-Trio (Session → "Session-<rate>"
  // at line 21), V-Muflis (Top 9 rate = win / stake + 1, e.g. 4 for a ×3 win),
  // V-Teenpatti 1-day ("Player A-Main" / "Player B-CON" / "Card 3 - Odd").
  const fmtType = { vtrio: 'trio', vteenmuf: 'teenmuf', vteen: 'teen' }[gameType] || gameType
  const [filter, setFilter] = useState('all')
  // Ticked rows (bet ids). None ticked = the totals cover every visible row.
  const [picked, setPicked] = useState(() => new Set())
  // B Details: the bet whose browser string is shown under its row
  const [openBrowser, setOpenBrowser] = useState(null)

  useEffect(() => { setPicked(new Set()); setOpenBrowser(null) }, [filter])

  const cols = showBhav ? 10 : 9

  // Deleted bets live only under their own tab — All/Back/Lay leave them out
  const visible = bets.filter((b) => {
    if (filter === 'deleted') return b.deleted
    if (b.deleted) return false
    if (filter === 'all') return true
    return filter === 'lay' ? isLay(b) : !isLay(b)
  })

  const counted = picked.size ? visible.filter((b) => picked.has(b.id)) : visible
  const totalWin = Math.round(counted.reduce((sum, b) => sum + (Number(b.win) || 0), 0) * 100) / 100

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  return (
    <div>
      {/* round-bets-head: on a phone the totals wrap under the radios and stay
          RIGHT-aligned (ml-auto), with the reference's gap above (CSS). */}
      {/* No table (no rows, emptyRow off): the reference still leaves its
          table wrapper's 8px under the radios (user 2026-10-15) → mb-2. */}
      <div className={`mt-2 d-flex flex-wrap align-items-center justify-content-between round-bets-head${emptyRow || visible.length > 0 ? '' : ' mb-2'}`}>
        <div>
          {FILTERS.map(([v, label]) => (
            <div key={v} className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id={`${idPrefix}-${v}`}
                name={`${idPrefix}-filter`}
                value={v}
                checked={filter === v}
                onChange={() => setFilter(v)}
                className="custom-control-input"
              />
              <label htmlFor={`${idPrefix}-${v}`} className="custom-control-label">{label}</label>
            </div>
          ))}
        </div>
        <h5 className="mb-0 ml-auto">
          Total Bets: <span className="text-success mr-2">{counted.length}</span> Total Win:{' '}
          <span className={totalWin < 0 ? 'text-danger' : 'text-success'}>{amount(totalWin)}</span>
        </h5>
      </div>
      {(emptyRow || visible.length > 0) && (<div className="table-responsive report-table mt-2">
        <table className="table">
          <thead>
            <tr>
              <th className="user-name"><div>Username</div></th>
              <th className="event-name"><div>Nation</div></th>
              <th className="text-right bet-user-rate"><div>Rate</div></th>
              {showBhav && <th className="text-right bet-user-rate"><div>Bhav</div></th>}
              <th className="text-right bet-amount"><div>Amount</div></th>
              <th className="text-right bet-amount"><div>Win</div></th>
              <th className="bet-date"><div>Date</div></th>
              <th><div>IP</div></th>
              <th><div>B Details</div></th>
              <th className="text-right"><div>Action</div></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={cols} className="text-center">No records found</td></tr>
            ) : visible.map((b) => {
              const win = Number(b.win)
              // A badlo leg is a swap leg, not a back/lay bet: no strip,
              // and the run given up reads as a negative amount
              const rowClass = b.badlo ? undefined : isLay(b) ? 'lay-border' : 'back-border'
              return [
                <tr key={b.id} className={rowClass}>
                  <td className="user-name"><div>{b.username}</div></td>
                  <td className="event-name"><div>{formatBetNation(fmtType, b.nation, b)}</div></td>
                  <td className="text-right bet-user-rate"><div>{formatBetOdds(fmtType, b, amount(b.rate), detail)}</div></td>
                  {showBhav && (
                    <td className="text-right bet-user-rate"><div>{b.bhav == null || b.bhav === '' ? '-' : amount(b.bhav)}</div></td>
                  )}
                  <td className="text-right bet-amount">
                    <div>{b.badlo && isLay(b) ? amount(-Math.abs(Number(b.amount) || 0)) : amount(b.amount)}</div>
                  </td>
                  {/* win is null while the bet is unsettled / deleted */}
                  <td className="text-right bet-amount">
                    <div className={b.win == null ? '' : win < 0 ? 'text-danger' : 'text-success'}>{b.win == null ? '' : amount(win)}</div>
                  </td>
                  <td className="bet-date"><div>{fmtDate(b.date)}</div></td>
                  <td><div>{b.ip}</div></td>
                  <td>
                    <a
                      href="#"
                      className="text-success"
                      title={b.browser}
                      onClick={(e) => { e.preventDefault(); setOpenBrowser((cur) => (cur === b.id ? null : b.id)) }}
                    >Detail</a>
                  </td>
                  <td className="text-right">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`${idPrefix}-chk-${b.id}`}
                        checked={picked.has(b.id)}
                        onChange={() => toggle(b.id)}
                      />
                      <label className="custom-control-label" htmlFor={`${idPrefix}-chk-${b.id}`}></label>
                    </div>
                  </td>
                </tr>,
                openBrowser === b.id ? (
                  <tr key={`${b.id}-browser`}>
                    <td colSpan={cols} className="text-muted">{b.browser || 'No browser details'}</td>
                  </tr>
                ) : null,
              ]
            })}
          </tbody>
        </table>
      </div>)}
    </div>
  )
}

export default RoundBetsTable
